import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { stockApi } from '../services/api';

function ManipulationTechniques() {
  const [activePhase, setActivePhase] = useState('accumulation');
  const [activeTechnique, setActiveTechnique] = useState(null);
  const [activePanel, setActivePanel] = useState({
    accumulation: 'lowAccumulation',
    washing: 'fakeBreakout',
    rising: 'quickRise',
    distribution: 'fakeBreakoutSell'
  });
  const [techniques, setTechniques] = useState({});
  const [indicators, setIndicators] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 获取操纵手法数据
        const techniquesResponse = await stockApi.getManipulationTechniques();
        if (techniquesResponse.success) {
          setTechniques(techniquesResponse.data);
          // 默认选中第一个阶段的第一个技术
          if (techniquesResponse.data.accumulation && techniquesResponse.data.accumulation.length > 0) {
            setActiveTechnique(techniquesResponse.data.accumulation[0]);
          }
        }

        // 获取操纵手法识别指标
        const indicatorsResponse = await stockApi.getManipulationIndicators();
        if (indicatorsResponse.success) {
          setIndicators(indicatorsResponse.data);
        }
      } catch (error) {
        console.error('获取操纵手法数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePhaseChange = (phase) => {
    setActivePhase(phase);
    if (techniques[phase] && techniques[phase].length > 0) {
      const defaultTechnique = techniques[phase].find(t => t.id === activePanel[phase]) || techniques[phase][0];
      setActiveTechnique(defaultTechnique);
    }
  };

  const handlePanelChange = (techniqueId) => {
    const technique = techniques[activePhase]?.find(t => t.id === techniqueId);
    if (technique) {
      setActiveTechnique(technique);
      setActivePanel({
        ...activePanel,
        [activePhase]: techniqueId
      });
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner">加载中...</div>
      </div>
    );
  }

  const phaseNames = {
    accumulation: '吸筹阶段',
    washing: '洗盘阶段',
    rising: '拉升阶段',
    distribution: '出货阶段'
  };

  const phaseTargets = {
    accumulation: '目标：在低位阶段收集足够筹码，而不引起市场关注',
    washing: '目标：清理意志不坚定的散户，为后续拉升做好准备',
    rising: '目标：快速拉高股价，吸引散户进场，推高市场情绪',
    distribution: '目标：在高位出货，将筹码卖给接盘散户，实现利润最大化'
  };

  const getTechniqueCount = (phase) => {
    return techniques[phase]?.length || 0;
  };

  return (
    <div className="container">
      {/* 面包屑导航 */}
      <div className="breadcrumbs">
        <Link to="/">首页</Link> <span>&gt;</span> <span>操盘手法</span> <span>&gt;</span> <span>{phaseNames[activePhase]}</span>
      </div>

      {/* 主导航 */}
      <nav className="main-nav">
        <div className="container">
          <ul className="main-nav-list">
            <li><a href="#" onClick={(e) => { e.preventDefault(); }}>庄家操作解码：</a></li>
            <li>
              <a 
                href="#" 
                className={activePhase === 'accumulation' ? 'active' : ''} 
                onClick={(e) => { e.preventDefault(); handlePhaseChange('accumulation'); }}
              >
                一 吸筹（建仓）
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className={activePhase === 'washing' ? 'active' : ''} 
                onClick={(e) => { e.preventDefault(); handlePhaseChange('washing'); }}
              >
                二 洗盘
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className={activePhase === 'rising' ? 'active' : ''} 
                onClick={(e) => { e.preventDefault(); handlePhaseChange('rising'); }}
              >
                三 拉升
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className={activePhase === 'distribution' ? 'active' : ''} 
                onClick={(e) => { e.preventDefault(); handlePhaseChange('distribution'); }}
              >
                四 出货
              </a>
            </li>
          </ul>
        </div>
      </nav>

      <div className="section-card">
        <div className="section-header">
          第{activePhase === 'accumulation' ? '一' : activePhase === 'washing' ? '二' : activePhase === 'rising' ? '三' : '四'}阶段：{phaseNames[activePhase]}
        </div>
        <div className="section-content">
          <div className="section-target">
            {phaseTargets[activePhase]}
          </div>
          
          {/* 子标签页导航 */}
          <div className="tab-nav">
            {techniques[activePhase]?.map(technique => (
              <button 
                key={technique.id}
                className={`tab-button ${activePanel[activePhase] === technique.id ? 'active' : ''}`}
                onClick={() => handlePanelChange(technique.id)}
              >
                {technique.name}
              </button>
            ))}
          </div>
          
          {/* 子内容面板 */}
          {techniques[activePhase]?.map(technique => (
            <div 
              key={technique.id}
              id={`panel-${technique.id}`} 
              className={`content-panel ${activePanel[activePhase] === technique.id ? 'active' : ''}`}
            >
              <div className="panel-column">
                <h3 className="panel-title">{technique.name} - 特征说明</h3>
                <ul className="panel-list">
                  {technique.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
              <div className="panel-column">
                <h3 className="panel-title">操作特点</h3>
                <ul className="panel-list">
                  {technique.operationPoints.map((operation, index) => (
                    <li key={index}>{operation}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
          
          {/* 数据统计区域 */}
          <div className="stats-section">
            <h3 className="stats-title">今日发现的处于"{phaseNames[activePhase]}"阶段的股票（{getTechniqueCount(activePhase)}只）</h3>
            <div className="stats-content">
              <div className="stats-icon">Ш</div>
            </div>
          </div>
          
          {/* 识别指标 */}
          <div className="indicators-section">
            <h3 className="indicators-title">操纵手法识别指标</h3>
            <div className="indicators-grid">
              {Object.entries(indicators).map(([category, items]) => (
                <div key={category} className="indicator-category">
                  <h3 className="category-title">
                    {category === 'volume' && '成交量指标'}
                    {category === 'price' && '价格指标'}
                    {category === 'pattern' && '形态指标'}
                    {category === 'capital' && '资金指标'}
                  </h3>
                  <ul className="indicator-list">
                    {items.map((indicator, index) => (
                      <li key={index} className={`indicator-item ${indicator.importance}`}>
                        <div className="indicator-name">{indicator.name}</div>
                        <div className="indicator-description">{indicator.description}</div>
                        <div className="indicator-importance">
                          重要性: 
                          <span className={`importance-level ${indicator.importance}`}>
                            {indicator.importance === 'high' && '高'}
                            {indicator.importance === 'medium' && '中'}
                            {indicator.importance === 'low' && '低'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .indicators-section {
          margin-top: 30px;
        }
        
        .indicators-title {
          font-size: 18px;
          margin-bottom: 15px;
          color: #1e3a8a;
        }
        
        .indicators-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
        }
        
        .indicator-category {
          border: 1px solid #e1e4e8;
          border-radius: 8px;
          padding: 15px;
          background-color: #f8f9fa;
        }
        
        .category-title {
          font-size: 16px;
          margin-bottom: 15px;
          color: #1e3a8a;
          border-bottom: 1px solid #e1e4e8;
          padding-bottom: 10px;
        }
        
        .indicator-list {
          list-style-type: none;
          padding-left: 0;
        }
        
        .indicator-item {
          margin-bottom: 15px;
          padding: 10px;
          border-radius: 6px;
          background-color: #eef3fd;
        }
        
        .indicator-item.high {
          border-left: 3px solid #e74c3c;
        }
        
        .indicator-item.medium {
          border-left: 3px solid #f39c12;
        }
        
        .indicator-item.low {
          border-left: 3px solid #3498db;
        }
        
        .indicator-name {
          font-weight: 500;
          margin-bottom: 5px;
        }
        
        .indicator-description {
          font-size: 14px;
          color: #666;
          margin-bottom: 5px;
        }
        
        .indicator-importance {
          font-size: 12px;
          color: #666;
        }
        
        .importance-level {
          font-weight: 500;
          margin-left: 5px;
        }
        
        .importance-level.high {
          color: #e74c3c;
        }
        
        .importance-level.medium {
          color: #f39c12;
        }
        
        .importance-level.low {
          color: #3498db;
        }
      `}</style>
    </div>
  );
}

export default ManipulationTechniques; 